import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { driver, type Driver, type PopoverDOM } from 'driver.js'
import { useAuthStore, type AuthUser } from '@/shared/stores/auth.store'
import { useToast } from '@/shared/components/ui/toast'
import type { AutoAdvanceHold, EndReason, StepView, Tour, TourAccessContext, TourSide, TourSource } from '../types'
import { emitTourEnd, getTourActivator, useWalkthroughStore } from '../stores/walkthrough.store'
import { WelcomeCard } from './welcome-card'
import { PageInviteCard } from './page-invite-card'
import { useTourContext } from '../hooks/use-tour-context'
import { TOURS, getTour } from '../lib/tours'
import { DEFAULT_WAIT_MS, TourRunner } from '../utils/tour-runner'
import { isHiddenNow, isVisible, waitForTarget } from '../utils/wait-for-target'
import { appDialogOpen, canLeavePage } from '../utils/onboarding'
import { isImpersonating } from '../utils/impersonation'
import { readDomState } from '../utils/page-invites'
import { ENGINE_COPY } from '../utils/engine-copy'
import {
  box,
  isTall,
  laidOut,
  lockedScrollOffset,
  parseStagePath,
  pointable,
  regionOf,
  scrollDelta,
  scrollInstantly,
  scrollParent,
  validateStepGeometry,
  visiblePart,
  whenStable,
} from '../utils/step-geometry'
import { copyToHtml, copyToPlain } from '../utils/rich-copy'
import {
  clearActive,
  getAutoAdvancePaused,
  getTourStatus,
  loadActive,
  saveActive,
  setAutoAdvancePaused,
  setTourStatus,
} from '../utils/progress-storage'
import { AutoAdvanceTimer, autoAdvanceMs } from '../utils/auto-advance'

/** Below this width the popover prefers bottom placement (sidebar is off-canvas ≤904px anyway). */
const NARROW_PX = 768
const STAGE_PADDING = 6
/** driver.js's highlight transition (its default `duration`). */
const TRANSITION_MS = 450
type StepSide = TourSide | undefined
/** A no-op stand-in for driver's own scrollIntoView (the host already placed the target). */
const noScroll = (): void => undefined

/** The side driver.js actually placed the popover on. */
const popoverSideNow = (): TourSide | 'over' | null => {
  const cls = [...(document.querySelector('.driver-popover')?.classList ?? [])].find((c) =>
    c.startsWith('driver-popover-side-'),
  )
  const side = cls?.slice('driver-popover-side-'.length)
  return side === 'top' || side === 'bottom' || side === 'left' || side === 'right' || side === 'over' ? side : null
}
const PAUSE_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor"/><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor"/></svg>'
const PLAY_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor"/></svg>'
/** Said once per run, with the first card that counts down. */
export const AUTO_ANNOUNCE = 'Cards advance automatically. Use Pause to stop.'
/** What a drawn card needs to arm its auto-advance (null = this card never auto-advances). */
interface AutoInfo {
  token: number
  stepId: string
  key: string
  ms: number
}
const hovered = (el: Element): boolean => {
  try {
    return el.matches(':hover')
  } catch {
    return false
  }
}

/** Only show the "one moment" card when a wait is long enough to notice. */
const LOADING_DELAY_MS = 300

/**
 * Only re-filter when the inputs of the access decision change — not on every
 * new `user` object (a profile mirror or WS refresh with identical grants).
 */
function accessKey(user: AuthUser | null, cached: string[], navGroups: TourAccessContext['navGroups']): string {
  return JSON.stringify([
    user?.id ?? null,
    [...(user?.permissions ?? cached)].sort(),
    [...(user?.buckets ?? [])].sort(),
    (user?.roles ?? []).map((r) => r.name).sort(),
    // Every boolean feature flag (not only today's two): a future `userFeature`
    // step re-filters when its flag changes (§21 #16).
    Object.entries(user?.features ?? {})
      .flatMap(([k, v]) => (typeof v === 'boolean' ? [`${k}=${v ? 1 : 0}`] : []))
      .sort(),
    [...(user?.access_request_types ?? [])].sort(),
    user?.active_client?.id ?? null,
    navGroups.length,
  ])
}

/** Said first when a step's target exists but can't be pointed at (M4). */
const CENTRED_PREFIX = ENGINE_COPY.centred
/** A target that never appeared (and has no `whenMissing` copy). */
/** Kept short so the step's own explanation still follows it (R4-2). */
const MISSING_PREFIX = ENGINE_COPY.missingPrefix

/** Load-error / not-available / 403 states: a page tour ends at once on these. */
const UNAVAILABLE_SELECTOR = '[data-tour-page-unavailable], [data-access-denied]'

const isHiddenOrAbsent = (selector: string) => {
  const el = document.querySelector(selector)
  return !el || !isVisible(el)
}

interface Props {
  /** Sidebar config from app/navigation.ts — passed in because features may not import app/. */
  navGroups: TourAccessContext['navGroups']
}

/**
 * Mounted once by AppShell (authenticated only). Owns the single driver.js
 * instance and the TourRunner; everything else (steps, copy, access rules)
 * comes from the registry. Renders only a visually hidden live region.
 */
export function WalkthroughHost({ navGroups }: Props) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const storeCtx = useTourContext()
  // Use the prop directly: the store copy is only published after the first effect.
  const ctx = useMemo(() => ({ ...storeCtx, navGroups }), [storeCtx, navGroups])
  const user = useAuthStore((s) => s.user)
  const cachedPermissions = useAuthStore((s) => s.cachedPermissions)
  const userId = user?.id ?? null
  const key = accessKey(user, cachedPermissions, navGroups)
  const request = useWalkthroughStore((s) => s.request)
  const setActiveTourId = useWalkthroughStore((s) => s.setActiveTourId)
  const setNavGroups = useWalkthroughStore((s) => s.setNavGroups)
  const consumeRequest = useWalkthroughStore((s) => s.consumeRequest)
  const { addToast } = useToast()
  const [announcement, setAnnouncement] = useState('')

  // Latest values for the long-lived runner callbacks.
  const live = useRef({ ctx, navigate, addToast, userId })
  useLayoutEffect(() => {
    live.current = { ctx, navigate, addToast, userId }
  })

  const runnerRef = useRef<TourRunner | null>(null)
  const startRef = useRef<((tour: Tour, source: TourSource, fromStepId?: string) => void) | null>(null)
  const commitWaiters = useRef(new Set<() => void>())
  /** The router's COMMITTED pathname (not window.location, which leads the render). */
  const committedPath = useRef(pathname)

  useEffect(() => {
    setNavGroups(navGroups)
  }, [navGroups, setNavGroups])

  // ── One driver + runner for the host's lifetime (StrictMode: create/destroy/create) ──
  useEffect(() => {
    let drv: Driver | null = null
    let loadingTimer: ReturnType<typeof setTimeout> | undefined
    let runUserId: string | null = null
    let runSource: TourSource = 'api'
    let restoreFocusTo: HTMLElement | null = null
    let unmounting = false
    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    let reduceMotion = motionQuery?.matches ?? false

    const onKeyUp = (e: KeyboardEvent) => {
      const runner = runnerRef.current
      if (!runner?.activeTourId) return
      if (e.key === 'ArrowRight') runner.next()
      else if (e.key === 'ArrowLeft') runner.back()
    }

    /**
     * `keepFocus`: an in-place upgrade re-renders the popover, and driver.js
     * focuses its first control. Put focus back where the user had it (the same
     * button in the new popover, or wherever it was) instead of jumping to Next.
     */
    const decorate = (
      popover: PopoverDOM,
      keepFocus?: { button: string | null; el: Element | null },
      auto?: AutoInfo | null,
    ) => {
      // Fill driver.js a11y gaps: it sets role=dialog + labelledby/describedby only.
      popover.wrapper.setAttribute('aria-modal', 'true')
      popover.progress.setAttribute('aria-hidden', 'true') // the live region announces it
      // Before driver measures the popover (onRender runs ahead of positioning).
      if (auto) mountAuto(popover)
      requestAnimationFrame(() => {
        if (keepFocus) {
          const same = keepFocus.button ? popover.wrapper.querySelector<HTMLElement>(`.${keepFocus.button}`) : null
          if (same) same.focus()
          else if (keepFocus.el instanceof HTMLElement && keepFocus.el.isConnected) keepFocus.el.focus()
        } else if (popover.nextButton.isConnected && popover.nextButton.style.display !== 'none') {
          // driver focuses the first focusable (the × close); land on the primary action instead.
          // (offsetParent is always null inside the position:fixed popover — check display instead.)
          popover.nextButton.focus()
        }
        // The card is drawn and focus is placed: only now may its countdown run.
        if (auto) armAuto(auto, popover.wrapper)
      })
    }

    /** Which popover button (if any) has focus right now. */
    const focusedPopoverButton = (): string | null => {
      const el = document.activeElement
      if (!el?.closest('.driver-popover')) return null
      for (const cls of ['driver-popover-next-btn', 'driver-popover-prev-btn', 'driver-popover-close-btn', 'mc-tour-auto-toggle']) {
        if (el.classList.contains(cls)) return cls
      }
      return null
    }

    const ensureDriver = (): Driver => {
      if (drv) return drv
      drv = driver({
        animate: !reduceMotion,
        // The host scrolls targets itself, instantly (see placeTarget): a smooth
        // scroll inside the app's scroll container outlived driver's measuring.
        smoothScroll: false,
        allowClose: true,
        overlayClickBehavior: 'none',
        overlayColor: 'rgb(8, 12, 22)',
        overlayOpacity: 0.55,
        stagePadding: STAGE_PADDING,
        stageRadius: 12,
        popoverOffset: 12,
        // The highlighted control can't be clicked — the tour only ever describes actions.
        disableActiveInteraction: true,
        allowKeyboardControl: true,
        popoverClass: 'mc-tour',
        // Esc (and any driver-initiated close) routes through the runner.
        onDestroyStarted: () => runnerRef.current?.close(),
      })
      window.addEventListener('keyup', onKeyUp)
      // driver.js only listens to WINDOW scroll; the app scrolls inside a container.
      // Capture catches every scroller (scroll doesn't bubble).
      document.addEventListener('scroll', onViewportChange, true)
      window.addEventListener('resize', onViewportChange)
      return drv
    }

    // ── Geometry: scroll deterministically, highlight once settled, follow the target ──
    let renderToken = 0
    let shown: { view: StepView | null; target: Element | null; tall: boolean; side: StepSide } = {
      view: null,
      target: null,
      tall: false,
      side: undefined,
    }
    // ── Auto-advance: a countdown on each eligible card that presses Next through the runner ──
    /** The user's explicit Pause (per user, read at each run start). */
    let autoPaused = false
    /**
     * The user drives the tour from the keyboard (Enter / Space / arrows / Tab, or
     * started it with a key, or activates a tour button the way a screen reader does —
     * a click with `detail === 0`): hold every card until they press Play. Per run,
     * never persisted. After that Play, keys no longer hold for this run.
     */
    let keyboardHeld = false
    let keyboardOptOut = false
    let lastActivationKeyAt = -Infinity
    /** A touch on the card's body: held until Play or the next card. */
    let touchHeld = false
    /** "Cards advance automatically…" is said once per run. */
    let autoAnnounced = false
    let autoUi: { wrapper: HTMLElement; fill: HTMLElement; secs: HTMLElement; toggle: HTMLButtonElement } | null = null
    /** The render the countdown was armed for: a timer that outlives it does nothing. */
    let autoToken = -1
    let autoStepId: string | null = null
    /** The user's focus hold as it was when a redraw began (a same-step redraw keeps it). */
    let focusCarry = false
    let secsTimer: ReturnType<typeof setInterval> | undefined

    /** What the toggle shows: paused (offers Play) for an explicit Pause or a keyboard / touch hold. */
    const autoOff = () => autoPaused || keyboardHeld || touchHeld

    const syncAutoUi = () => {
      clearInterval(secsTimer)
      secsTimer = undefined
      const ui = autoUi
      if (!ui?.wrapper.isConnected) return
      const duration = autoTimer.durationMs
      const left = autoTimer.remaining()
      // Stable name; aria-pressed="true" = auto-advance is paused (the icon offers Play).
      ui.toggle.setAttribute('aria-pressed', String(autoOff()))
      ui.toggle.innerHTML = autoOff() ? PLAY_ICON : PAUSE_ICON
      ui.wrapper.dataset.autoAdvance = autoTimer.running ? 'running' : autoOff() ? 'paused' : 'held'
      const showSecs = () => {
        ui.secs.textContent = `${Math.ceil(autoTimer.remaining() / 1000)}s`
      }
      showSecs()
      ui.fill.style.transition = 'none'
      ui.fill.style.transform = `scaleX(${duration ? left / duration : 0})`
      if (!autoTimer.running) return
      // Reduced motion: no moving bar — a remaining-seconds label, updated in place.
      if (reduceMotion) secsTimer = setInterval(showSecs, 250)
      else {
        void ui.fill.getBoundingClientRect() // commit the start point before animating
        ui.fill.style.transition = `transform ${left}ms linear`
        ui.fill.style.transform = 'scaleX(0)'
      }
    }

    // Every transition (setPending → stopAuto) and every redraw (render) moves
    // renderToken / autoToken apart, so this one check covers a late timer.
    const autoTimer = new AutoAdvanceTimer(() => {
      if (autoToken === renderToken) runnerRef.current?.next() // exactly what Next does
    }, syncAutoUi)

    const stopAuto = () => {
      autoTimer.clear()
      clearInterval(secsTimer)
      secsTimer = undefined
      autoUi = null
      autoStepId = null
      autoToken = -1
    }

    const onVisibility = () => autoTimer.hold('hidden', document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    const onMotionChange = (e: MediaQueryListEvent) => {
      reduceMotion = e.matches
      syncAutoUi()
    }
    motionQuery?.addEventListener?.('change', onMotionChange)

    const KEYBOARD_KEYS = new Set(['Enter', ' ', 'ArrowLeft', 'ArrowRight', 'Tab'])
    const holdForKeyboard = (target: EventTarget | null) => {
      if (!runnerRef.current?.activeTourId || keyboardOptOut || keyboardHeld) return
      // The toggle's own press is the toggle's business (Pause / Play).
      if (target instanceof Element && target.closest('.mc-tour-auto-toggle')) return
      keyboardHeld = true
      autoTimer.hold('keyboard', true)
      syncAutoUi()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (!KEYBOARD_KEYS.has(e.key)) return
      if (e.key === 'Enter' || e.key === ' ') lastActivationKeyAt = Date.now()
      holdForKeyboard(e.target)
    }
    // VoiceOver VO+Space / NVDA-JAWS browse-mode Enter send a click with no keydown; detail 0 marks it.
    const onClick = (e: MouseEvent) => {
      if (e.detail === 0 && e.target instanceof Element && e.target.closest('.driver-popover button')) holdForKeyboard(e.target)
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('click', onClick, true)

    /** The countdown bar + Pause/Play control, added to the card before driver measures it. */
    const mountAuto = (popover: PopoverDOM) => {
      const wrapper = popover.wrapper
      const group = document.createElement('span')
      group.className = 'mc-tour-auto'
      const toggle = document.createElement('button')
      toggle.type = 'button'
      toggle.className = 'mc-tour-auto-toggle'
      toggle.setAttribute('aria-label', 'Pause auto-advance')
      const secs = document.createElement('span')
      secs.className = 'mc-tour-auto-secs'
      secs.setAttribute('aria-hidden', 'true')
      group.append(toggle, secs)
      popover.progress.after(group)
      const bar = document.createElement('div')
      bar.className = 'mc-tour-countdown'
      bar.setAttribute('aria-hidden', 'true')
      const fill = document.createElement('div')
      fill.className = 'mc-tour-countdown-fill'
      bar.append(fill)
      wrapper.append(bar)
      toggle.addEventListener('click', () => {
        if (autoOff()) {
          // Play: one press resumes from whatever paused it. Only an explicit
          // Pause was persisted, so only that is un-persisted.
          if (autoPaused && runUserId) setAutoAdvancePaused(runUserId, false)
          if (keyboardHeld) keyboardOptOut = true
          autoPaused = keyboardHeld = touchHeld = false
          // Play is the user saying "go on": their focus on the card no longer holds it.
          for (const h of ['paused', 'keyboard', 'touch', 'focus'] as const) autoTimer.hold(h, false)
          setAnnouncement('Auto-advance resumed')
        } else {
          autoPaused = true
          if (runUserId) setAutoAdvancePaused(runUserId, true) // no-op while impersonating
          autoTimer.hold('paused', true)
          setAnnouncement('Auto-advance paused')
        }
        syncAutoUi()
      })
      wrapper.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'touch') autoTimer.hold('hover', true)
      })
      wrapper.addEventListener('pointerleave', () => autoTimer.hold('hover', false))
      // Touch has no hover: a touch on the card's text holds it (buttons do their own thing).
      wrapper.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'touch' || (e.target instanceof Element && e.target.closest('button'))) return
        touchHeld = true
        autoTimer.hold('touch', true)
        syncAutoUi()
      })
      // The host's own focus placement happens before arming (arm replaces the holds);
      // a focus move after that is the user's. The toggle itself never holds.
      wrapper.addEventListener('focusin', (e) => {
        if (e.target !== toggle) autoTimer.hold('focus', true)
      })
      wrapper.addEventListener('focusout', (e) => {
        if (!wrapper.contains(e.relatedTarget as Node | null)) autoTimer.hold('focus', false)
      })
      autoUi = { wrapper, fill, secs, toggle }
    }

    const armAuto = (auto: AutoInfo, wrapper: HTMLElement) => {
      const runner = runnerRef.current
      // Next pressed before this card's settle frame: the runner is already moving on.
      if (auto.token !== renderToken || !wrapper.isConnected || !runner?.activeTourId || runner.isPending) return
      touchHeld = false
      const holds: AutoAdvanceHold[] = []
      if (autoPaused) holds.push('paused')
      if (keyboardHeld) holds.push('keyboard')
      if (document.hidden) holds.push('hidden')
      if (hovered(wrapper)) holds.push('hover')
      if (focusCarry && autoStepId === auto.stepId && wrapper.contains(document.activeElement)) holds.push('focus')
      autoToken = auto.token
      autoStepId = auto.stepId
      autoTimer.arm(auto.key, auto.ms, holds)
    }

    /** Scroll positions the engine changed, restored when the tour ends (the page is left as found). */
    const scrolledFrom = new Map<Element, { top: number; left: number }>()
    const rememberScroll = (el: Element) => {
      if (!scrolledFrom.has(el)) scrolledFrom.set(el, { top: el.scrollTop, left: el.scrollLeft })
    }
    const restoreScrolls = () => {
      for (const [el, at] of scrolledFrom) {
        if (!el.isConnected) continue
        if (typeof el.scrollTo === 'function')
          el.scrollTo({ top: at.top, left: at.left, behavior: 'instant' as ScrollBehavior })
        else Object.assign(el, { scrollTop: at.top, scrollLeft: at.left })
      }
      scrolledFrom.clear()
    }
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    let proxy: HTMLDivElement | null = null
    let targetObserver: ResizeObserver | null = null
    let refreshFrame = 0

    /** Fixed, invisible stand-in for the visible top of a target taller than the room left. */
    const syncProxy = (target: Element): HTMLDivElement => {
      if (!proxy) {
        proxy = document.createElement('div')
        proxy.className = 'mc-tour-proxy'
        proxy.setAttribute('aria-hidden', 'true')
        document.body.appendChild(proxy)
      }
      const part =
        visiblePart(box(target.getBoundingClientRect()), regionOf(scrollParent(target)), shown.side)?.part ??
        box(target.getBoundingClientRect())
      Object.assign(proxy.style, {
        top: `${part.top}px`,
        left: `${part.left}px`,
        width: `${part.width}px`,
        height: `${part.height}px`,
      })
      return proxy
    }
    const dropProxy = () => {
      proxy?.remove()
      proxy = null
    }

    /**
     * Re-measure stage AND popover. driver.js's refresh() places both against
     * its *active* element, which it only switches at the END of its highlight
     * transition — a refresh fired mid-transition (a tab switch re-laying out
     * the pane) would park the popover against the previous step. So retry
     * until driver is on our element. A target re-mounted under us (activator,
     * refetch) is re-resolved and re-drawn.
     */
    function onViewportChange() {
      scheduleRefresh(0)
    }
    function scheduleRefresh(tries: number) {
      if (!drv || (refreshFrame && tries === 0)) return
      refreshFrame = requestAnimationFrame(() => {
        refreshFrame = 0
        const { view, target } = shown
        if (view && target && !target.isConnected) {
          const fresh = view.step.target ? document.querySelector(view.step.target) : null
          if (fresh && pointable(fresh)) render({ ...view, element: fresh })
          return
        }
        if (shown.tall && target) syncProxy(target)
        const expected = shown.tall ? proxy : target
        if (expected && drv?.getActiveElement() !== expected && tries < 60) return scheduleRefresh(tries + 1)
        drv?.refresh()
      })
    }

    /** Dev / `?tourDebug`: `window.__tourGeometry()` → the current step's rects + validator errors (browser QA). */
    const geometryNow = () => {
      const target = shown.target
      const rect = (el: Element | null | undefined) => (el ? box(el.getBoundingClientRect()) : null)
      const g = {
        stepId: runnerRef.current?.activeTourId
          ? (document.querySelector('.driver-popover-title')?.textContent ?? null)
          : null,
        target: rect(target),
        highlight: rect(shown.tall ? proxy : target),
        stage: parseStagePath(document.querySelector('.driver-overlay path')?.getAttribute('d')),
        popover: rect(document.querySelector('.driver-popover')),
        popoverSide: popoverSideNow(),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        visibleRegion: target?.isConnected ? regionOf(scrollParent(target)) : null,
        lockedScrollOffset: target?.isConnected ? lockedScrollOffset(target) : 0,
        stagePadding: STAGE_PADDING,
      }
      return { ...g, errors: validateStepGeometry(g) }
    }
    // Dev builds only (QA runs against the dev server); never exposed in production.
    const debugGeometry = import.meta.env.DEV
    if (debugGeometry) (window as unknown as { __tourGeometry?: typeof geometryNow }).__tourGeometry = geometryNow

    const observeTarget = (el: Element | null) => {
      targetObserver?.disconnect()
      targetObserver = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onViewportChange) : null
      if (el) targetObserver?.observe(el)
    }

    /**
     * `restoreScroll`: put back the scroll positions the engine changed — only
     * when the user ENDED the tour here (Finish, close, Esc, or the tour itself
     * running out). After a navigation abort the browser owns the new page's
     * scroll (Back restores it), and on unmount (logout) there is nothing to restore.
     */
    const teardownDriver = (restoreScroll = false) => {
      clearTimeout(loadingTimer)
      stopAuto()
      window.removeEventListener('keyup', onKeyUp)
      document.removeEventListener('scroll', onViewportChange, true)
      window.removeEventListener('resize', onViewportChange)
      cancelAnimationFrame(refreshFrame)
      refreshFrame = 0
      renderToken++
      clearTimeout(settleTimer)
      observeTarget(null)
      dropProxy()
      if (restoreScroll) restoreScrolls()
      else scrolledFrom.clear()
      shown = { view: null, target: null, tall: false, side: undefined }
      if (drv) {
        drv.destroy()
        drv = null
      }
    }

    /**
     * Scroll the target into place INSTANTLY in its own scroll container (room
     * for the popover on its side; a tall target shows its top), wait until its
     * rect holds still for 2 frames (bounded), THEN highlight — never measure a
     * moving target.
     */
    const render = (view: StepView) => {
      clearTimeout(loadingTimer)
      const token = ++renderToken
      // Never count down while a card is being (re)drawn; arming happens once it is settled.
      focusCarry = autoTimer.isHeld('focus')
      autoTimer.hold('drawing', true)
      const el = view.element
      const side = window.innerWidth < NARROW_PX ? 'bottom' : view.step.side
      const deltaNow = () =>
        el && laidOut(el) ? scrollDelta(box(el.getBoundingClientRect()), regionOf(scrollParent(el)), side) : 0
      if (!el || !deltaNow()) return paint(view)
      void (async () => {
        // Re-check after settling: pages re-lay out on scroll (a header that
        // collapses once scrolled), so one jump can land short. Bounded.
        for (let i = 0; i < 3; i++) {
          const delta = deltaNow()
          if (!delta || !el.isConnected) break
          const scroller = scrollParent(el)
          rememberScroll(scroller)
          scrollInstantly(scroller, delta)
          await whenStable(el)
          if (token !== renderToken) return
        }
        if (el.isConnected) return paint(view)
        // Re-mounted during the wait (refetch, tab/layout swap): draw the new node,
        // or the step's note — never leave the previous step's popover up (M18-1).
        const fresh = view.step.target ? document.querySelector(view.step.target) : null
        paint(fresh && pointable(fresh) ? { ...view, element: fresh } : { ...view, element: null, centred: 'missing' })
      })()
    }

    const paint = (view: StepView) => {
      const d = ensureDriver()
      const narrow = window.innerWidth < NARROW_PX
      const { step, position, total } = view
      const keepFocus = view.upgraded ? { button: focusedPopoverButton(), el: document.activeElement } : undefined
      const title = view.blockedNavigation ? ENGINE_COPY.blockedTitle : step.title
      const altHidden = step.bodyIfHidden && isHiddenOrAbsent(step.bodyIfHidden.selector)
      const targetKind =
        view.element && step.bodyByTargetAttr ? view.element.getAttribute(step.bodyByTargetAttr.attribute) : null
      const ctxNow = live.current.ctx
      const stateBody = altHidden
        ? step.bodyIfHidden!.body
        : (targetKind && step.bodyByTargetAttr?.bodies[targetKind]) ||
          (view.state && step.bodyByState?.[view.state]) ||
          step.bodyFrom?.(ctxNow) ||
          step.body
      const body = view.blockedNavigation
        ? ENGINE_COPY.blockedBody(step.title)
        : view.centred === 'missing'
          ? (step.whenMissing ?? `${MISSING_PREFIX} ${stateBody}`)
          : view.centred
            ? (step.fallbackFrom?.(ctxNow) ?? step.fallbackBody ?? `${CENTRED_PREFIX[view.centred]} ${stateBody}`)
            : stateBody
      // Auto-advance: never on the last card (Finish is the user's), a blocked
      // "opens another page" card or a centred fallback note.
      const autoText = `${copyToPlain(title)} ${copyToPlain(body)}`
      const auto: AutoInfo | null =
        view.isLast || view.blockedNavigation || view.centred
          ? null
          : {
              token: renderToken,
              stepId: step.id,
              key: `${view.tour.id}|${step.id}|${position}/${total}|${autoText}`,
              ms: autoAdvanceMs(autoText),
            }
      if (!auto) stopAuto()
      const el = view.element
      const wantSide: StepSide = narrow ? 'bottom' : step.side
      // A target that scrolls itself (the sidebar nav) is shown from its start.
      if (el && el.scrollTop > 0 && el.scrollHeight > el.clientHeight) {
        rememberScroll(el)
        el.scrollTo?.({ top: 0, behavior: 'instant' as ScrollBehavior })
      }
      const targetBox = el && laidOut(el) ? box(el.getBoundingClientRect()) : null
      const region = el ? regionOf(scrollParent(el)) : null
      const tallSide =
        targetBox && region && isTall(targetBox, region, wantSide)
          ? (visiblePart(targetBox, region, wantSide)?.side ?? 'bottom')
          : null
      const tall = !!tallSide
      if (!tall) dropProxy()
      shown = { view, target: el, tall, side: tallSide ?? wantSide }
      observeTarget(el)
      const highlighted = tall ? syncProxy(el!) : el
      // driver.js scrollIntoView()s a target not fully in the window (inline:
      // 'center'), which also scrolls overflow-hidden ancestors SIDEWAYS — a
      // shift the user can't undo. The host already placed it: no driver scroll.
      const ownScroll = highlighted ? Object.getOwnPropertyDescriptor(highlighted, 'scrollIntoView') : undefined
      if (highlighted)
        Object.defineProperty(highlighted, 'scrollIntoView', { value: noScroll, configurable: true, writable: true })
      try {
        d.highlight({
          element: highlighted ?? undefined,
          popover: {
            // Escaped, then "quoted" labels in bold without the quotes (rich-copy.ts).
            title: copyToHtml(title),
            description: copyToHtml(body),
            // A tall target: next to its highlighted visible part (step-geometry visiblePart).
            side: view.element ? (tallSide ?? (narrow ? 'bottom' : step.side)) : undefined,
            align: 'start',
            showButtons: view.isFirst ? ['next', 'close'] : ['next', 'previous', 'close'],
            showProgress: true,
            progressText: `${position} of ${total}`,
            nextBtnText: view.isLast ? 'Finish' : 'Next',
            prevBtnText: 'Back',
            closeBtnLabel: 'Close tour',
            onNextClick: () => runnerRef.current?.next(),
            onPrevClick: () => runnerRef.current?.back(),
            onCloseClick: () => runnerRef.current?.close(),
            onPopoverRender: (p) => decorate(p, keepFocus, auto),
          },
        })
      } finally {
        if (highlighted) {
          if (ownScroll) Object.defineProperty(highlighted, 'scrollIntoView', ownScroll)
          else delete (highlighted as { scrollIntoView?: unknown }).scrollIntoView
        }
      }
      // Once driver's transition is over and the layout has settled (an
      // activator's tab switch re-lays out the pane), re-place stage + popover.
      clearTimeout(settleTimer)
      if (el) {
        const token = renderToken
        settleTimer = setTimeout(() => {
          if (token === renderToken) void whenStable(el).then(() => token === renderToken && onViewportChange())
        }, TRANSITION_MS)
      }
      const said = view.upgraded
        ? `${copyToPlain(title)} is now on the page and highlighted. Step ${position} of ${total}.`
        : `Step ${position} of ${total}: ${copyToPlain(title)}`
      // Once per run, on the first card that will actually count down.
      const tellAuto = !!auto && !autoAnnounced && !autoOff()
      if (tellAuto) autoAnnounced = true
      setAnnouncement(tellAuto ? `${said} ${AUTO_ANNOUNCE}` : said)
    }

    const setPending = (pending: boolean) => {
      clearTimeout(loadingTimer)
      if (!pending) return
      // A transition (Next, Back, navigation, a wait) always starts the next card from full.
      stopAuto()
      loadingTimer = setTimeout(() => {
        const runner = runnerRef.current
        if (!runner?.isPending || !runner.activeTourId) return
        ensureDriver().highlight({
          popover: {
            title: ENGINE_COPY.loading.title,
            description: ENGINE_COPY.loading.body,
            showButtons: ['close'],
            closeBtnLabel: 'Close tour',
            onCloseClick: () => runnerRef.current?.close(),
            onPopoverRender: (p) => p.wrapper.setAttribute('aria-modal', 'true'),
          },
        })
        setAnnouncement('Loading the next step')
      }, LOADING_DELAY_MS)
    }

    const onEnd = (tour: { id: string; version: number }, reason: EndReason, shownAny: boolean) => {
      teardownDriver(reason !== 'aborted' && !unmounting)
      setActiveTourId(null)
      setAnnouncement('')
      const uid = runUserId
      if (uid && !unmounting) {
        clearActive(uid)
        if (reason === 'completed') setTourStatus(uid, tour, 'completed')
        else if (reason === 'skipped') setTourStatus(uid, tour, 'skipped')
      }
      // Walkthrough-owned toasts are plain text: "quoted" labels lose their quotes.
      const toast: typeof live.current.addToast = (t) =>
        live.current.addToast({
          ...t,
          title: copyToPlain(t.title),
          description: t.description && copyToPlain(t.description),
        })
      if (reason === 'unavailable') {
        toast({
          variant: 'info',
          title: ENGINE_COPY.toast.unavailableTitle,
          description: shownAny ? ENGINE_COPY.toast.unavailableRest : ENGINE_COPY.toast.unavailableNothing,
        })
      } else if (reason === 'page_loading') {
        toast({
          variant: 'info',
          title: ENGINE_COPY.toast.pageLoadingTitle,
          description: ENGINE_COPY.toast.pageLoading,
        })
      } else if (reason === 'page_unavailable') {
        // Say what happened, neutrally: only the 403 panel is about the user's role.
        toast({
          variant: 'info',
          title: ENGINE_COPY.toast.unavailableTitle,
          description: document.querySelector('[data-access-denied]')
            ? ENGINE_COPY.toast.pageForbidden
            : ENGINE_COPY.toast.pageDown,
        })
      } else if (reason === 'access_lost') {
        toast({ variant: 'info', title: ENGINE_COPY.toast.accessLostTitle, description: ENGINE_COPY.toast.accessLost })
      }
      if (reason !== 'aborted' && !unmounting) {
        // The launching menu item has unmounted by now; fall back to the ? trigger.
        const back = restoreFocusTo?.isConnected
          ? restoreFocusTo
          : document.querySelector<HTMLElement>('[data-tour="header.help"]')
        back?.focus()
      }
      emitTourEnd({
        tourId: tour.id,
        outcome: reason === 'completed' ? 'completed' : reason === 'skipped' ? 'dismissed' : 'aborted',
        source: runSource,
      })
      runUserId = null
      restoreFocusTo = null
    }

    const runner = new TourRunner({
      getContext: () => live.current.ctx,
      tours: TOURS,
      currentPath: () => committedPath.current,
      canLeave: canLeavePage,
      navigate: (path, signal) =>
        new Promise<boolean>((resolve) => {
          const finish = (committed: boolean) => {
            commitWaiters.current.delete(onCommit)
            clearTimeout(timer)
            signal.removeEventListener('abort', onAbort)
            resolve(committed)
          }
          const onCommit = () => finish(true)
          const onAbort = () => finish(false)
          // A commit that never lands (stuck lazy chunk) is a failure, not a success.
          const timer = setTimeout(onAbort, DEFAULT_WAIT_MS)
          signal.addEventListener('abort', onAbort, { once: true })
          commitWaiters.current.add(onCommit)
          void live.current.navigate(path)
        }),
      waitFor: (selector, ms, signal, opts) => waitForTarget(selector, ms, signal, opts),
      isHiddenNow,
      isPresent: (selector) => !!document.querySelector(selector),
      unavailableSelector: UNAVAILABLE_SELECTOR,
      getActivator: getTourActivator,
      readState: readDomState,
      render,
      setPending,
      onStepShown: (tour, step, path) => {
        setActiveTourId(tour.id)
        // No resume records while impersonating: exiting (a full reload) runs no
        // cleanup, and the record must never follow that identity (QA F6).
        if (!runUserId || isImpersonating()) return
        saveActive(runUserId, {
          tourId: tour.id,
          version: tour.version,
          stepId: step.id,
          path,
          source: runSource,
          savedAt: Date.now(),
        })
        if (getTourStatus(runUserId, tour) !== 'in_progress') setTourStatus(runUserId, tour, 'in_progress')
      },
      onEnd,
    })

    runnerRef.current = runner
    // Every start goes through here: tag the run with its user so persistence
    // never crosses accounts, and remember where focus should return.
    startRef.current = (tour, source, fromStepId) => {
      // Fail closed: no signed-in user = no access decision yet → nothing to show.
      if (!useAuthStore.getState().user) return
      runner.stop('aborted')
      runUserId = live.current.userId
      runSource = source
      keyboardHeld = keyboardOptOut = false // per run
      autoPaused = runUserId ? getAutoAdvancePaused(runUserId) : false
      autoAnnounced = false
      // Started with Enter / Space (the ? menu item): a keyboard run from card one.
      if (!keyboardOptOut && Date.now() - lastActivationKeyAt < 1000) keyboardHeld = true
      const active = document.activeElement
      restoreFocusTo = active instanceof HTMLElement && active !== document.body ? active : null
      runner.start(tour, fromStepId)
    }

    // Resume after a refresh: same user, same tour version, within the TTL, and
    // on the SAME page the step was shown on. A full load elsewhere (typed URL,
    // deep link, re-login) drops the record instead of guiding the wrong page.
    const uid = live.current.userId
    if (uid && isImpersonating()) clearActive(uid)
    const rec = uid ? loadActive(uid) : null
    const resumeTour = rec ? getTour(rec.tourId) : undefined
    if (rec && resumeTour?.version === rec.version && rec.path === committedPath.current) {
      startRef.current(resumeTour, rec.source, rec.stepId)
    } else if (uid && rec) {
      clearActive(uid)
    }

    return () => {
      unmounting = true
      // Logged out (AppShell swaps to <Navigate> and unmounts us before the
      // identity effect can run): the next sign-in must not resume this run.
      const leaving = runUserId ?? live.current.userId
      if (leaving && !useAuthStore.getState().isAuthenticated) clearActive(leaving)
      runner.stop('aborted')
      teardownDriver()
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('click', onClick, true)
      motionQuery?.removeEventListener?.('change', onMotionChange)
      if (debugGeometry) delete (window as unknown as { __tourGeometry?: unknown }).__tourGeometry
      runnerRef.current = null
      startRef.current = null
    }
  }, [setActiveTourId])

  // ── Start requests (menu, onboarding, anything calling startTour) ──
  useEffect(() => {
    if (!request) return
    consumeRequest()
    const tour = getTour(request.tourId)
    if (!startRef.current || !tour) return
    if (appDialogOpen()) {
      live.current.addToast({
        variant: 'info',
        title: ENGINE_COPY.toast.dialogOpenTitle,
        description: ENGINE_COPY.toast.dialogOpen,
      })
      return
    }
    startRef.current(tour, request.source)
  }, [request, consumeRequest])

  // ── Router commits: release navigation waits, detect user-driven navigation ──
  useEffect(() => {
    committedPath.current = pathname
    for (const done of [...commitWaiters.current]) done()
    runnerRef.current?.onLocationChange(pathname)
  }, [pathname])

  // ── Identity: a different user (or logout / impersonation) ends the run ──
  const lastUser = useRef(userId)
  useEffect(() => {
    if (lastUser.current === userId) return
    const previous = lastUser.current
    lastUser.current = userId
    runnerRef.current?.stop('aborted')
    if (previous) clearActive(previous)
  }, [userId])

  // ── Permissions / entitlements / active client changed: re-filter & re-check ──
  const lastKey = useRef(key)
  useEffect(() => {
    if (lastKey.current === key) return
    lastKey.current = key
    runnerRef.current?.refilter()
  }, [key])

  return (
    <>
      <div className="mc-tour-live" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      <WelcomeCard />
      <PageInviteCard />
    </>
  )
}
